import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Router, CanActivate } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';

import { SubscriptionService } from './subscription.service';
import { BrandService } from '../brand/brand.service';
import { UserService } from '../user/user.service';
import { PermissionsService } from '../permissions/permissions.service';
import { TermsService } from '../terms/terms.service';
import { Config } from '../config/env.config';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';


@Injectable()
export class SubscriptionGuard implements CanActivate {
  constructor(
    private router: Router,
    private subscriptionService: SubscriptionService,
    private brandService: BrandService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private termsService: TermsService,
  ) {}

  canActivate(): Observable<boolean>|boolean {
    if (Config.ALLOW_NON_SUBSCRIBERS) {
        return true;
    } else {

      return new Observable(observer => {
        const brandId = this.brandService.getBrand();

        forkJoin([
          this.subscriptionService.getSubscription(brandId),
          this.userService.getUser(),
          this.termsService.hasAcceptedLatest(),
          this.subscriptionService.getAccountBrand(brandId),
        ])
        .subscribe((res) => {
          let canActivate: boolean;
          let redirectTo = null;
          const subscription = res[0];
          const user = res[1];
          const terms = res[2];
          const accountBrand = res[3];

          if (user.is_rxspeed) {
            /*
              * Let RxSpeed users through to the full PIM
              */
            canActivate = true;
          } else if (_.get(accountBrand, 'paywall_on_at') && moment().isBefore(accountBrand.paywall_on_at)) {
            /*
              * If paywall_on_at set and paywall_on_at is less than current time
              * then the paywall is OFF
              */
            canActivate = true;
          } else {
            /*
              * Check if we have a valid subscription
              */
            const validSubscription = subscription.valid;
            const termsAccepted = !terms.needs_acceptance;

            if (validSubscription && termsAccepted) {
              canActivate = true;
            } else {
              canActivate = false;
            /*
              * Check if we have an admin user logged in.
              */
              if (this.permissionsService.hasPermission(['admin'])) {
                if (!validSubscription) {
                  /*
                    * This is admin user. We want to redirect user to the
                    * Get Started page to collect billing info.
                    */
                  redirectTo = '/get-started';
                } else {
                  if (!termsAccepted) {
                    redirectTo = '/account-information/billing';
                    this.termsService.callAccept();
                  }
                }
              } else {
                /*
                  * This is a non admin user. We will deny them access
                  * to edit and download actions.
                  */
                canActivate = true;

                // remove catalog-edit, dam-edit, repository
                this.permissionsService.getPermissions()
                  .subscribe((permissions) => {
                    if (_.isArray(permissions)) {
                      const allowedPermissions: string[] = permissions.filter(p => !['catalog-edit', 'dam-edit', 'repository'].includes(p));
                      this.permissionsService.storePermissions(allowedPermissions);
                    }
                  });
              }
            }
            if (Config.BUGSNAG_KEY) {
              localStorage.setItem('accountBrand', JSON.stringify(accountBrand));
              localStorage.setItem('subscription', JSON.stringify(subscription));
              localStorage.setItem('terms', JSON.stringify(terms));
              localStorage.setItem('userInfo', JSON.stringify(user));
            }
          }
          observer.next(canActivate);
          observer.complete();

          /*
            * Redirect user if needed.
            */
          if (redirectTo) {
            this.router.navigate([redirectTo]);
          }
        });

      });
    }
  }
}
